import api from './api';

/**
 * Resize image to 400x400 (center crop) and return as File
 */
export function resizeImageTo400x400(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas not supported'));
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      const dataUrl = e.target?.result as string;
      img.onload = () => {
        const SIZE = 400;
        canvas.width = SIZE;
        canvas.height = SIZE;

        // Center crop: draw source image scaled to cover, then crop center
        const scale = Math.max(SIZE / img.width, SIZE / img.height);
        const scaledW = img.width * scale;
        const scaledH = img.height * scale;
        const sx = (scaledW - SIZE) / 2;
        const sy = (scaledH - SIZE) / 2;
        ctx.drawImage(img, -sx, -sy, scaledW, scaledH);

        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Failed to resize image'));
              return;
            }
            const resizedFile = new File([blob], 'category-400x400.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          },
          'image/jpeg',
          0.92
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload category image to S3 (folder: category-images/)
 * Image is resized to 400x400 before upload.
 */
export async function uploadCategoryImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('image', file);
  const res = await api.post<{
    success: boolean;
    data?: { url: string };
    message?: string;
  }>('/admin/upload/category', formData, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      },
    ],
  });
  if (!res.data.success || !res.data.data?.url) {
    throw new Error(
      (res.data as { message?: string }).message || 'Upload thất bại'
    );
  }

  return res.data.data.url;
}

/** Response from dish-cover upload: url (raw, for DB) + signedUrl (for display) */
export interface DishCoverUploadResult {
  url: string;
  signedUrl: string;
}

/**
 * Upload dish cover image to S3 (folder: dish-covers/)
 * Returns url (raw, for DB) and signedUrl (for preview - S3 private bucket cần signed URL).
 */
export async function uploadDishCoverImage(
  file: File
): Promise<DishCoverUploadResult> {
  const formData = new FormData();
  formData.append('image', file);
  const res = await api.post<{
    success: boolean;
    data?: { url: string; signedUrl?: string };
    message?: string;
  }>('/admin/upload/dish-cover', formData, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData) {
          delete headers['Content-Type'];
        }
        return data;
      },
    ],
  });
  const data = res.data?.data;
  const url = data?.url ?? null;
  const signedUrl = data?.signedUrl ?? url;
  if (!res.data?.success || !url) {
    throw new Error(
      (res.data as { message?: string })?.message || 'Upload thất bại'
    );
  }
  return { url, signedUrl: signedUrl || url };
}
